import React, { useState } from 'react';

import { LOCALES, LOCALE_LABELS, getLocale, setLocale, t, type Locale } from '../i18n/strings';

/**
 * UnlockSurface — the identity gate.
 *
 * Three modes:
 *   - generate : new identity, returns seed for user to back up
 *   - unlock   : existing seed + password
 *   - restore  : encrypted backup blob + password
 */

export type NetworkMode = 'local' | 'mesh';
const NETWORK_MODE_KEY = 'alds-network-mode';

function loadNetworkMode(): NetworkMode {
  try {
    const v = localStorage.getItem(NETWORK_MODE_KEY);
    if (v === 'mesh' || v === 'local') return v;
  } catch { /* localStorage may be unavailable */ }
  return 'local';
}

/**
 * Inline feedback for the seed input so the user can see WHY the submit
 * button is disabled. Pasted seeds frequently pick up whitespace or
 * truncate; without inline feedback the user just sees a dead button.
 */
function seedStatus(seed: string): string {
  if (seed.length === 0) return 'paste a 64-character hex seed';
  if (/[^0-9a-f]/i.test(seed)) {
    const nonHex = seed.replace(/[0-9a-f]/gi, '').slice(0, 8);
    return `⚠ non-hex characters detected (e.g. "${nonHex}") — clean paste only [0-9a-f]`;
  }
  if (seed.length < 64) return `need ${64 - seed.length} more hex chars (have ${seed.length})`;
  if (seed.length > 64) return `⚠ too long: ${seed.length} chars (need exactly 64)`;
  return '✓ valid 64-char hex seed';
}

interface Props {
  onSubmit: (params: {
    mode: 'generate' | 'unlock' | 'restore';
    password: string;
    seed?: string;
    backup?: Uint8Array;
    networkMode: NetworkMode;
  }) => void | Promise<void>;
  error: string | null;
}

export function UnlockSurface({ onSubmit, error }: Props): JSX.Element {
  const [mode, setMode] = useState<'generate' | 'unlock' | 'restore'>('generate');
  const [password, setPassword] = useState('');
  const [seed, setSeed] = useState('');
  const [busy, setBusy] = useState(false);
  const [networkMode, setNetworkMode] = useState<NetworkMode>(loadNetworkMode);
  // Bumped when the locale changes so the surface re-renders with new strings.
  const [, setLocaleTick] = useState(0);

  const canSubmit = (() => {
    if (busy) return false;
    if (!password) return false;
    if (mode === 'unlock' && !/^[0-9a-f]{64}$/i.test(seed)) return false;
    return true;
  })();

  const pickNetwork = (m: NetworkMode) => {
    setNetworkMode(m);
    try { localStorage.setItem(NETWORK_MODE_KEY, m); } catch { /* noop */ }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const params: Parameters<Props['onSubmit']>[0] =
        mode === 'unlock'
          ? { mode, password, seed, networkMode }
          : { mode, password, networkMode };
      await onSubmit(params);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="unlock-wrap">
      <style>{styles}</style>
      <div className="unlock-surface schematic-frame">
        <div className="t-meta unlock-id">
          <span className="pulse-dot" /> set up your identity
        </div>
        <h2 className="t-serif unlock-q">Set up your identity</h2>
        <p className="unlock-sub">
          Your passphrase unlocks a private key that only lives in this browser tab.
          Nothing is saved to disk in the clear, and even the operator can&apos;t read it.
        </p>

        <div className="mode-tabs">
          {(['generate', 'unlock', 'restore'] as const).map((m) => (
            <button
              key={m}
              className={'mode-tab' + (mode === m ? ' active' : '')}
              onClick={() => setMode(m)}
            >
              {m}
            </button>
          ))}
        </div>

        {mode === 'generate' && (
          <div className="mode-body">
            <p className="t-meta mode-detail">
              fresh Ed25519 root · seed shown after boot · back it up
            </p>
          </div>
        )}

        {mode === 'unlock' && (
          <div className="mode-body">
            <label className="t-meta">seed (64 hex)</label>
            <input
              type="text"
              value={seed}
              onChange={(e) => {
                // Sanitize aggressively: paste from a wrapped <div> can pick
                // up newlines/spaces; some apps prepend `0x`. Strip both.
                let v = e.target.value.replace(/\s+/g, '');
                if (v.startsWith('0x') || v.startsWith('0X')) v = v.slice(2);
                setSeed(v.toLowerCase());
              }}
              placeholder="0123456789abcdef…"
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
            <div className="t-meta seed-status">{seedStatus(seed)}</div>
          </div>
        )}

        {mode === 'restore' && (
          <div className="mode-body">
            <p className="t-meta mode-detail">
              encrypted backup import — not implemented in alpha
            </p>
          </div>
        )}

        <div className="mode-body">
          <label className="t-meta">password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="passphrase"
            autoComplete="new-password"
          />
        </div>

        <div className="mode-body">
          <label className="t-meta" htmlFor="locale-select">{t('common.locale')}</label>
          <select
            id="locale-select"
            value={getLocale()}
            onChange={(e) => {
              setLocale(e.target.value as Locale);
              // Re-render the surface by flipping a state knob.
              setLocaleTick((n) => n + 1);
            }}
            style={{ width: '100%' }}
          >
            {LOCALES.map((loc) => (
              <option key={loc} value={loc}>{LOCALE_LABELS[loc]}</option>
            ))}
          </select>
        </div>

        <div className="mode-body">
          <label className="t-meta">network mode</label>
          <div className="net-tabs" role="radiogroup" aria-label="network mode">
            <button
              className={'mode-tab' + (networkMode === 'local' ? ' active' : '')}
              role="radio"
              aria-checked={networkMode === 'local'}
              onClick={() => pickNetwork('local')}
            >local-only · recommended</button>
            <button
              className={'mode-tab' + (networkMode === 'mesh' ? ' active' : '')}
              role="radio"
              aria-checked={networkMode === 'mesh'}
              onClick={() => pickNetwork('mesh')}
            >mesh (experimental)</button>
          </div>
          <p className="t-meta mode-detail" style={{ marginTop: 8 }}>
            {networkMode === 'local'
              ? 'in-memory single-node — fastest, all flows work · DEFAULT'
              : '⚠ experimental: libp2p + Helia + OrbitDB pipeline. If unlock fails, switch to local-only.'}
          </p>
        </div>

        {error && (
          <div className="unlock-error">
            {error}
            {networkMode === 'mesh' && (/Blockstore|async iterable|libp2p|helia|orbit/i.test(error)) && (
              <div style={{ marginTop: 8, fontSize: 11, fontStyle: 'normal' }}>
                <strong>Workaround:</strong> mesh mode is experimental.{' '}
                <button
                  className="pill"
                  style={{ display: 'inline-block', marginLeft: 4, padding: '2px 8px' }}
                  onClick={() => pickNetwork('local')}
                >switch to local-only</button>{' '}
                then click unlock again. Local-only handles every flow except cross-NAT peer discovery.
              </div>
            )}
          </div>
        )}

        <div className="unlock-actions">
          <button
            className="cta"
            disabled={!canSubmit || mode === 'restore'}
            onClick={() => void submit()}
          >
            {mode === 'generate' ? 'generate & unlock' : 'unlock'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = `
.unlock-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 20px;
}
.unlock-surface {
  width: min(520px, 100%);
  max-width: 100%;
  background: var(--bg-2);
  padding: 36px 32px;
}
@media (max-width: 600px) {
  .unlock-wrap { padding: 8px; }
  .unlock-surface { padding: 20px 16px; }
  .unlock-q { font-size: 22px !important; }
  .mode-tabs { flex-wrap: wrap; }
  .net-tabs { flex-wrap: wrap; }
  .net-tabs .mode-tab { flex: 1 1 calc(50% - 4px); }
}
.unlock-id {
  display: flex; gap: 12px; align-items: center;
  margin-bottom: 18px;
}
.unlock-q {
  font-size: 30px;
  margin: 0 0 8px;
  line-height: 1.1;
}
.unlock-sub {
  font-size: 11px;
  color: var(--ink-dim);
  line-height: 1.6;
  margin: 0 0 24px;
}
.mode-tabs {
  display: flex;
  gap: 6px;
  border-bottom: 1px dashed var(--rule);
  padding-bottom: 12px;
  margin-bottom: 16px;
}
.mode-tab {
  padding: 6px 12px;
  font-size: 10px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  color: var(--ink-dim);
  border: 1px solid var(--rule);
}
.mode-tab.active { color: var(--phos); border-color: var(--phos); }
.mode-tab:hover:not(.active) { color: var(--ink); border-color: var(--rule-bright); }
.net-tabs { display: flex; gap: 6px; }
.net-tabs .mode-tab { flex: 1; text-align: center; }
.mode-body { margin-bottom: 14px; }
.mode-body label { display: block; margin-bottom: 6px; }
.mode-body input { width: 100%; }
.seed-status {
  margin-top: 4px;
  font-size: 10px;
  color: var(--ink-dim);
  letter-spacing: 0.05em;
  text-transform: none;
  word-break: break-all;
}
.mode-detail { color: var(--ink-dim); font-style: italic; }
.unlock-error {
  margin: 8px 0 16px;
  padding: 10px 12px;
  border: 1px solid var(--red);
  color: var(--red);
  font-size: 11px;
}
.unlock-actions { margin-top: 8px; }
`;
